import React, { useEffect, useMemo, useState, useId, Component } from 'react';
/**
 * SubSkillCheatSheet — Renders hints and visual aids for a specific family/skill.
 *
 * Visual treatment: hint cards are styled like sticky notes / index cards
 * on a notebook page — each hintType has its own pastel paper colour, a
 * custom inline-SVG "stamp" on the left (Djay-style, no platform emoji),
 * and a small category chip. The card sits on the cream "paper" background
 * of `CheatSheetDrawer` so the whole thing reads as a real cheat-sheet
 * rather than a generic dialog.
 *
 * Progressive disclosure (v1.4 — May 2026)
 * ----------------------------------------
 * The first hint (after hintType ordering — typically `core`) renders as a
 * primary, always-expanded card. Any remaining hints render BELOW a small
 * "Meer hulp" divider as collapsed cards that show only the type chip +
 * title; content unfurls on tap. Rationale:
 *   • A kid (groep 7-8) who asks for help wants ONE clear answer first.
 *   • Pitfalls and transfers are valuable context but compete with the core
 *     rule when shown as equal-weight cards.
 *   • Collapsing keeps the drawer scannable on phones (CLT, Sweller).
 *   • The chip+title stays visible so the kid can predict what's inside
 *     before tapping (Mayer's Signaling Principle).
 *
 * Empty state (no hints):
 *   Warm DjayAvatar + UniversalStrategyCard as primary content. Here density
 *   is fine because there is no competing content.
 *
 * Co-located in support/components/ (RC Ronde 5 — S-3: verhuisd uit
 * practice/components/. Renders support-domain data and belongs hier.)
 */
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { type HintStep, type HintType, getHintsForFamilyId } from '..';
import { VisualAidFactory } from '../../visuals';
import { LatexRenderer } from '../../components/ui/LatexRenderer';
import { DjayAvatar, useDjaySelection } from '../../mascot';
import { UniversalStrategyCard } from './UniversalStrategyCard';
import { warn } from '../../utils/logger';
interface SubSkillCheatSheetProps {
  familyId?: string;
}
/** Ordering priority for hint types. Lower number = earlier in the list. */
const HINT_TYPE_ORDER: Record<HintType, number> = {
  core: 0,
  example: 1,
  pitfall: 2,
  transfer: 3
};
/**
 * Per-hintType styling tuned for the "paper / sticky note" cheat-sheet
 * aesthetic. Each note has:
 *  - a pastel paper fill that sits comfortably on the cream drawer surface
 *  - a coloured top-edge "tab" to identify the hint category at a glance
 *  - a custom SVG stamp + chip
 */
const HINT_TYPE_META: Record<HintType, {
  label: string;
  cardBg: string;
  cardBorder: string;
  tabBg: string;
  chipBg: string;
  chipText: string;
  /** Tailwind text colour for the SVG stamp (currentColor source). */
  stampText: string;
}> = {
  core: {
    label: 'Kern',
    cardBg: 'bg-indigo-50/60',
    cardBorder: 'border-indigo-200/70',
    tabBg: 'bg-indigo-400',
    chipBg: 'bg-indigo-100',
    chipText: 'text-indigo-700',
    stampText: 'text-indigo-500'
  },
  example: {
    label: 'Voorbeeld',
    cardBg: 'bg-amber-50/70',
    cardBorder: 'border-amber-200/70',
    tabBg: 'bg-amber-400',
    chipBg: 'bg-amber-100',
    chipText: 'text-amber-800',
    stampText: 'text-amber-600'
  },
  pitfall: {
    label: 'Let op',
    cardBg: 'bg-rose-50/70',
    cardBorder: 'border-rose-200/70',
    tabBg: 'bg-rose-400',
    chipBg: 'bg-rose-100',
    chipText: 'text-rose-700',
    stampText: 'text-rose-500'
  },
  transfer: {
    label: 'Verbinding',
    cardBg: 'bg-emerald-50/70',
    cardBorder: 'border-emerald-200/70',
    tabBg: 'bg-emerald-400',
    chipBg: 'bg-emerald-100',
    chipText: 'text-emerald-700',
    stampText: 'text-emerald-600'
  }
};
/**
 * HintTypeGlyph — Custom Djay-style inline SVG stamps, one per HintType.
 *
 * No platform emoji (each OS renders 🧠/💡/⚠️/🔗 differently) and no
 * lucide icons (too generic / app-default look). Each glyph is a small
 * hand-drawn marker mark that fits the paper aesthetic:
 *
 *   • core      → marker-stijl letter "K" met scribble-underline
 *   • example   → vier-puntige sparkle (eureka-flash)
 *   • pitfall   → driehoek-met-uitroepteken (stempel-stijl)
 *   • transfer  → curly arrow-loop (verbinding tussen twee punten)
 *
 * Strokes use `currentColor` so the parent `meta.stampText` Tailwind class
 * drives the colour. The parent tile already applies `rotate(-3deg)` for
 * the geïnktee stempel-feel — no extra rotation here.
 *
 * Dimensies: 22×22 viewBox; stroke-width 2 voor zichtbaarheid in 8-9px tile.
 */
function HintTypeGlyph({
  type
}: {
  type: HintType;
}) {
  switch (type) {
    case 'core':
      return <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-11263">
          {/* Letter K — twee diagonalen vanaf één verticale stam */}
          <path d="M6 4 V18" data-id="element-11264" />
          <path d="M6 11 L14 4" data-id="element-11265" />
          <path d="M6 11 L15 18" data-id="element-11266" />
          {/* Hand-drawn underline — kleine wiggle voor marker-feel */}
          <path d="M4 20 Q 8 19 11 20 T 18 20" strokeWidth="1.5" opacity="0.55" data-id="element-11267" />
        </svg>;
    case 'example':
      return <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-11268">
          {/* Vier-puntige sparkle — "eureka!" */}
          <path d="M11 3 L11 9" data-id="element-11269" />
          <path d="M11 13 L11 19" data-id="element-11270" />
          <path d="M3 11 L9 11" data-id="element-11271" />
          <path d="M13 11 L19 11" data-id="element-11272" />
          {/* Klein centerpunt */}
          <circle cx="11" cy="11" r="1.2" fill="currentColor" stroke="none" data-id="element-11273" />
          {/* Twee subtiele kleine vonken in de hoeken */}
          <path d="M5 5 L6.5 6.5" strokeWidth="1.4" opacity="0.55" data-id="element-11274" />
          <path d="M15.5 15.5 L17 17" strokeWidth="1.4" opacity="0.55" data-id="element-11275" />
        </svg>;
    case 'pitfall':
      return <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-11276">
          {/* Driehoek (waarschuwingsbordje, stempel-stijl) */}
          <path d="M11 3.5 L19 18 L3 18 Z" data-id="element-11277" />
          {/* Uitroepteken in het midden — bovenste streep */}
          <path d="M11 9 L11 13.5" strokeLinecap="round" data-id="element-11278" />
          {/* Onderste dot */}
          <circle cx="11" cy="15.8" r="1" fill="currentColor" stroke="none" data-id="element-11279" />
        </svg>;
    case 'transfer':
      return <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-11280">
          {/* Curly arrow-loop — start linksonder, loop, eindigt rechts met pijlpunt */}
          <path d="M4 16 Q 4 7 11 7 Q 18 7 18 13" data-id="element-11281" />
          {/* Pijlpunt rechts */}
          <path d="M14.5 11 L18 13 L16 16.5" strokeLinejoin="round" data-id="element-11282" />
          {/* Klein vertrekpunt-dot linksonder */}
          <circle cx="4" cy="16" r="1" fill="currentColor" stroke="none" data-id="element-11283" />
        </svg>;
  }
}
export function SubSkillCheatSheet({
  familyId
}: SubSkillCheatSheetProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const rawHints: HintStep[] = familyId ? getHintsForFamilyId(familyId) : [];
  const hints = useMemo(() => [...rawHints].map((h, originalIndex) => ({
    h,
    originalIndex
  })).sort((a, b) => {
    const aType = a.h.hintType ?? 'core';
    const bType = b.h.hintType ?? 'core';
    const diff = HINT_TYPE_ORDER[aType] - HINT_TYPE_ORDER[bType];
    return diff !== 0 ? diff : a.originalIndex - b.originalIndex;
  }).map(x => x.h), [rawHints]);
  // Collapsed-state tracking for the secondary hints. Indices refer to the
  // sorted `hints` array. We default to empty (all secondary hints collapsed).
  // Reset when familyId changes so opening a new family starts clean.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  useEffect(() => {
    setExpanded(new Set());
  }, [familyId]);
  // ── Empty state: warm strategy scaffold ────────────────────
  if (hints.length === 0) {
    return <div role="status" aria-live="polite" data-id="element-11284">
        <div className="text-center pt-2 pb-4" data-id="element-11285">
          <div className="flex justify-center mb-3" data-id="element-11286">
            <DjayAvatar size="lg" emotion="encouraging" variant={selectedVariant} data-id="element-11287" />
          </div>
          <p className="text-sm font-semibold text-slate-700 max-w-xs mx-auto leading-snug" data-id="element-11288">
            Geen kant-en-klaar spiekbriefje, maar je kunt altijd dit proberen:
          </p>
        </div>
        <UniversalStrategyCard variant="primary" data-id="element-11289" />
      </div>;
  }
  // ── Normal state: primary card + collapsible secondaries ──
  const [primaryHint, ...secondaryHints] = hints;
  const toggleExpanded = (index: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);else next.add(index);
      return next;
    });
  };
  return <div className="space-y-3" aria-label="Hints" data-id="element-11290">
      {/* Primary card — always visible, the "answer" the kid came for */}
      <HintCard hint={primaryHint} familyId={familyId} data-id="element-11291" />

      {secondaryHints.length > 0 && <>
          {/* Subtle divider with label — signals "this is extra, optional" */}
          <div className="flex items-center gap-2 pt-2 pb-1" data-id="element-11292">
            <div className="h-px flex-1 bg-amber-200/60" aria-hidden="true" data-id="element-11293" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-700/70" data-id="element-11294">
              Meer hulp
            </span>
            <div className="h-px flex-1 bg-amber-200/60" aria-hidden="true" data-id="element-11295" />
          </div>

          <ul className="space-y-2.5" data-id="element-11296">
            {secondaryHints.map((hint, i) => {
          const index = i + 1; // index in the full sorted array
          const isOpen = expanded.has(index);
          return <li key={index} data-id="element-11297">
                  <CollapsibleHintCard hint={hint} familyId={familyId} isOpen={isOpen} onToggle={() => toggleExpanded(index)} data-id="element-11298" />
                </li>;
        })}
          </ul>
        </>}
    </div>;
}
/**
 * Primary always-expanded hint card. Used for the first hint (typically
 * the `core` rule) — the one the kid actually came for.
 */
function HintCard({
  hint,
  familyId
}: {
  hint: HintStep;
  familyId?: string;
}) {
  const type: HintType = hint.hintType ?? 'core';
  const meta = HINT_TYPE_META[type];
  return <div className={`relative ${meta.cardBg} border ${meta.cardBorder} rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_-6px_rgba(15,23,42,0.08)]`} data-id="element-11299">
      {/* Top "tab" — the coloured edge of the sticky note */}
      <div aria-hidden="true" className={`absolute top-0 left-0 right-0 h-1 ${meta.tabBg}`} data-id="element-11300" />
      <div className="flex items-start gap-3 px-4 pt-4 pb-3.5" data-id="element-11301">
        <div aria-hidden="true" className={`flex-shrink-0 w-9 h-9 rounded-xl bg-white/80 border border-white shadow-sm flex items-center justify-center ${meta.stampText}`} style={{
        transform: 'rotate(-3deg)'
      }} data-id="element-11302">
          <HintTypeGlyph type={type} data-id="element-11303" />
        </div>
        <div className="min-w-0 flex-1" data-id="element-11304">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-11305">
            {hint.title && <h4 className="text-sm font-extrabold text-slate-900 leading-snug" data-id="element-11306">
                <LatexRenderer content={hint.title} data-id="element-11307" />
              </h4>}
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.chipBg} ${meta.chipText}`} data-id="element-11308">
              {meta.label}
            </span>
          </div>
          <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-11309">
            <LatexRenderer content={hint.content} data-id="element-11310" />
          </div>
          {hint.visual && <HintVisualSlot hint={hint} familyId={familyId} data-id="element-11311" />}
        </div>
      </div>
    </div>;
}
/**
 * Collapsible hint card. Renders as a tappable strip with chip + title +
 * chevron when closed; expands to show full content (and any visual) when
 * opened. Uses a real <button> for the trigger so keyboard + screen reader
 * users get correct semantics out of the box.
 */
function CollapsibleHintCard({
  hint,
  familyId,
  isOpen,
  onToggle
}: {
  hint: HintStep;
  familyId?: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const type: HintType = hint.hintType ?? 'core';
  const meta = HINT_TYPE_META[type];
  // Stable ids for aria-controls / aria-labelledby pairing.
  const reactId = useId();
  const panelId = `hint-panel-${reactId}`;
  const triggerId = `hint-trigger-${reactId}`;
  return <div className={`relative ${meta.cardBg} border ${meta.cardBorder} rounded-2xl overflow-hidden`} data-id="element-11312">
      <div aria-hidden="true" className={`absolute top-0 left-0 right-0 h-1 ${meta.tabBg}`} data-id="element-11313" />
      <button type="button" id={triggerId} aria-expanded={isOpen} aria-controls={panelId} onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/30 active:bg-white/50 transition-colors" data-id="element-11314">
        <div aria-hidden="true" className={`flex-shrink-0 w-8 h-8 rounded-lg bg-white/80 border border-white shadow-sm flex items-center justify-center ${meta.stampText}`} style={{
        transform: 'rotate(-3deg)'
      }} data-id="element-11315">
          <HintTypeGlyph type={type} data-id="element-11316" />
        </div>
        <div className="min-w-0 flex-1" data-id="element-11317">
          <div className="flex items-center gap-2 flex-wrap" data-id="element-11318">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.chipBg} ${meta.chipText}`} data-id="element-11319">
              {meta.label}
            </span>
            {hint.title && <span className="text-sm font-bold text-slate-800 leading-snug truncate" data-id="element-11320">
                <LatexRenderer content={hint.title} data-id="element-11321" />
              </span>}
          </div>
        </div>
        <motion.div aria-hidden="true" animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="flex-shrink-0 text-slate-500" data-id="element-11322">
          <ChevronDown className="w-4 h-4" data-id="element-11323" />
        </motion.div>
      </button>
      <AnimatePresence initial={false} data-id="element-11324">
        {isOpen && <motion.div id={panelId} role="region" aria-labelledby={triggerId} initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: 'easeOut'
      }} className="overflow-hidden" data-id="element-11325">
            <div className="px-4 pb-4 pt-1 pl-[60px]" data-id="element-11326">
              <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-11327">
                <LatexRenderer content={hint.content} data-id="element-11328" />
              </div>
              {hint.visual && <HintVisualSlot hint={hint} familyId={familyId} data-id="element-11329" />}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
/**
 * F-H7: Isolate a hint's visual rendering so that a malformed `HintVisual`
 * (e.g. wrong shape passed into `VisualAidFactory`) cannot crash the entire
 * Spiekbriefje. We use a tiny class boundary because `VisualAidFactory` is
 * deep enough that a per-renderer try/catch would be brittle.
 */
class HintVisualSlot extends Component<{
  hint: HintStep;
  familyId?: string;
}, {
  failed: boolean;
}> {
  state = {
    failed: false
  };
  static getDerivedStateFromError() {
    return {
      failed: true
    };
  }
  componentDidCatch(error: Error) {
    warn(`[SubSkillCheatSheet] visual render failed for family ${this.props.familyId ?? '?'}: ${error.message}`);
  }
  render() {
    if (this.state.failed || !this.props.hint.visual) return null;
    return <div className="mt-3 bg-white/70 rounded-xl p-2 border border-white" data-id="element-11330">
        <VisualAidFactory visualAid={this.props.hint.visual} isCorrect={undefined} mode="support" data-id="element-11331" />
      </div>;
  }
}